import { http } from './client';

// -----------------------------------------------------------------------------
// Response shapes (mirror api/src/routes/dashboard.ts)
// -----------------------------------------------------------------------------
export interface KpiStrip {
  beneficiaries:        { value: number; delta: string };
  subsidy_disbursed:    { value_cr: number; sub: string };
  active_applications:  { value: number; sub: string };
  sla_breaches:         { value: number; delta: string; red: boolean };
  active_maitri:        { value: number; sub: string };
  q4_camps:             { value: number; sub: string };
}

export interface MandalCell {
  code: string;
  name: string;
  districts: number;
  beneficiaries: number;
  verified: number;
  disbursed_cr: number;
  open_alerts: number;
  health: number;
  color: 'green' | 'amber' | 'red';
  trend: string;
}

export interface SchemePipeline {
  schemes: Array<{
    code: 'GOAT' | 'SHEEP' | 'PIG';
    name: string;
    target: number;
    verified: number;
    subsidy_released: number;
    pipeline: number;
    disbursed_cr: number;
    unit_cost_inr: number;
  }>;
  poultry: { cvo_review: number; pre_appraisal: number; appraisal: number; final_approval: number };
}

export interface FinancialPhysicalRow {
  mandal_name: string;
  disbursed_cr: number;
  verified_units: number;
}

export interface RecommendationOpen {
  id: string;
  category: string;
  target: string;
  text: string;
  primary_action: string;
  confidence: number;
  source: string;
  created_at: string;
}
export interface RecommendationsSummary {
  open: RecommendationOpen[];
  stats: { open: number; accepted: number; rejected: number; acceptance_rate: number };
}

export interface ActivityItem {
  id: number;
  time: string;
  actor: string;
  action: string;
  target: string | null;
  module: string | null;
  meta: unknown;
  timestamp: string;
}

// -----------------------------------------------------------------------------
// Fetchers
// -----------------------------------------------------------------------------
export const dashboardApi = {
  kpiStrip: async (): Promise<KpiStrip> => (await http.get('/dashboard/kpi-strip')).data.data,
  mandalHeatmap: async (): Promise<MandalCell[]> => (await http.get('/dashboard/mandal-heatmap')).data.data,
  schemePipeline: async (): Promise<SchemePipeline> => (await http.get('/dashboard/scheme-pipeline')).data.data,
  financialPhysical: async (): Promise<FinancialPhysicalRow[]> => (await http.get('/dashboard/financial-physical')).data.data,
  recommendationsSummary: async (): Promise<RecommendationsSummary> => (await http.get('/dashboard/recommendations-summary')).data.data,
  activityFeed: async (limit = 10): Promise<ActivityItem[]> =>
    (await http.get('/dashboard/activity-feed', { params: { limit } })).data.data,
};

// -----------------------------------------------------------------------------
// Mandal drill-down
// -----------------------------------------------------------------------------
export interface MandalDetail {
  code: string;
  name: string;
  districts_count: number;
  beneficiaries: number;
  verified: number;
  pipeline: number;
  disbursed_cr: number;
  districts: Array<{ name: string; beneficiaries: number; disbursed_lakhs: number; open_alerts: number }>;
  scheme_split: Record<string, number>;
}
export const mandalApi = {
  detail: async (code: string): Promise<MandalDetail> => (await http.get(`/mandals/${code}`)).data.data,
};

// -----------------------------------------------------------------------------
// Alerts
// -----------------------------------------------------------------------------
export interface AlertSummary {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  module: string;
  title: string;
  description: string;
  owner: string;
  escalation_role: string;
  status: string;
  triggered_at: string;
  source_rule: string;
  mandal: { code: string; name: string } | null;
  district: string | null;
}
export interface AlertDetail extends AlertSummary {
  acknowledged_at: string | null;
  resolved_at: string | null;
  metadata: unknown;
  actions: Array<{ type: string; notes: string | null; performed_at: string; actor: string; role: string | null }>;
}

export const alertsApi = {
  list: async (status: string = 'open', pageSize = 10): Promise<AlertSummary[]> => {
    const { data } = await http.get('/alerts', { params: { status, page_size: pageSize } });
    return data.data;
  },
  detail: async (code: string): Promise<AlertDetail> => (await http.get(`/alerts/${code}`)).data.data,
  action: async (code: string, action: string, notes?: string): Promise<{ new_status: string }> => {
    const { data } = await http.post(`/alerts/${code}/actions`, { action, notes });
    return data.data;
  },
};
